import { makeLinkComponent } from 'braid-design-system';
import { InternalLink } from 'scoobie';
import { useElementPosition } from '#src/hooks/ElementPositionProvider.tsx';
import { useTracking } from '#src/hooks/tracking/TrackingProvider.tsx';

const isExternalHref = (href: string) =>
  /^[a-z][a-z0-9+.-]*:|^\/\//i.test(href);

export const TealiumTrackableLink = makeLinkComponent(
  ({ children, href, download, ...restProps }, ref) => {
    const { trackLink } = useTracking();
    const elementPosition = useElementPosition();

    const handleClick = (
      elementAction: string,
      e: React.MouseEvent,
      elementDesign: string,
    ) => {
      const elementText =
        e.target instanceof HTMLElement
          ? e.currentTarget.textContent || ''
          : '';

      trackLink({
        eventName: 'developer_site_element_pressed',
        data: {
          elementLink: href,
          elementAction,
          elementDesign,
          elementText,
          elementPosition,
        },
      });
    };

    if (isExternalHref(href)) {
      return (
        <a
          rel="noreferrer"
          target="_blank"
          {...restProps}
          download={download}
          href={href}
          ref={ref}
          onClick={(e) => {
            restProps.onClick?.(e);
            handleClick('external navigation', e, restProps.role ?? 'link');
          }}
        >
          {children}
        </a>
      );
    }

    if (download) {
      return (
        <a
          {...restProps}
          href={href}
          ref={ref}
          download={download}
          onClick={(e) => {
            restProps.onClick?.(e);
            handleClick('download', e, 'button');
          }}
        >
          {children}
        </a>
      );
    }

    return (
      <InternalLink
        {...restProps}
        href={href}
        ref={ref}
        reset={false}
        onClick={(e) => {
          restProps.onClick?.(e);
          handleClick('internal navigation', e, restProps.role ?? 'link');
        }}
      >
        {children}
      </InternalLink>
    );
  },
);
